import React, { useState, useEffect, useRef } from "react";
import styled, { DefaultTheme } from "styled-components";
import ModalPortal from "src/modalPortal";
import iconCheck from "src/assets/images/iconCheck.svg";
import iconCheckActive from "src/assets/images/iconCheckActive.svg";

interface GalleryImageModalProps {
  images: any[];
  initialIndex: number;
  onClose: () => void;
  onSelect: (image: any, storeItem: any) => void;
  isSelected: (storeItem: any, image: any) => boolean;
  canSelect: (storeItem: any, image: any) => boolean;
  storeItem: any;
  imageServerIp: string;
}

const GalleryImageModal: React.FC<GalleryImageModalProps> = ({
  images,
  initialIndex,
  onClose,
  onSelect,
  isSelected,
  canSelect,
  storeItem,
  imageServerIp,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    
    const handleKeyNav = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (e.key === "ArrowRight" && currentIndex < images.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    };

    document.addEventListener("keydown", handleEsc);
    document.addEventListener("keydown", handleKeyNav);
    
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.removeEventListener("keydown", handleKeyNav);
    };
  }, [onClose, currentIndex, images.length]);

  // 터치 스와이프 처리
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // 마우스 드래그 처리
  const [mouseStart, setMouseStart] = useState<number | null>(null);
  const [mouseEnd, setMouseEnd] = useState<number | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    setMouseEnd(null);
    setMouseStart(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (mouseStart !== null) {
      setMouseEnd(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (!mouseStart || !mouseEnd) return;
    
    const distance = mouseStart - mouseEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
    
    setMouseStart(null);
    setMouseEnd(null);
  };

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (storeItem && canSelect(storeItem, images[currentIndex])) {
      onSelect(images[currentIndex], storeItem);
    }
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  if (!storeItem) return null;

  return (
    <ModalPortal>
      <ModalOverlay ref={modalRef} onClick={handleBackdropClick}>
        <ModalContent>
          <CloseButton type="button" aria-label="닫기" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </CloseButton>

          <ImageContainer
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <MainImage 
              src={imageServerIp + images[currentIndex].img_url} 
              alt={`이미지 ${currentIndex + 1}`}
              draggable={false}
            />
          
            {canSelect(storeItem, images[currentIndex]) && (
              <SelectButton 
                className={isSelected(storeItem, images[currentIndex]) ? "selected" : ""}
                onClick={handleSelectClick}
              >
                {isSelected(storeItem, images[currentIndex]) ? (
                  <img src={iconCheckActive} alt="선택됨" />
                ) : (
                  <img src={iconCheck} alt="선택" />
                )}
              </SelectButton>
            )}
          </ImageContainer>

          <ImageCounter>
            {currentIndex + 1} / {images.length}
          </ImageCounter>

          {images.length > 1 && (
            <>
              {currentIndex > 0 && (
                <NavButton 
                  className="prev" 
                  onClick={() => setCurrentIndex(currentIndex - 1)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </NavButton>
              )}
            
              {currentIndex < images.length - 1 && (
                <NavButton 
                  className="next" 
                  onClick={() => setCurrentIndex(currentIndex + 1)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </NavButton>
              )}
            </>
          )}

          <ThumbnailStrip>
            {images.map((img, index) => (
              <Thumbnail
                key={index}
                className={index === currentIndex ? "active" : ""}
                onClick={() => handleThumbnailClick(index)}
              >
                <img 
                  src={imageServerIp + img.img_url} 
                  alt={`썸네일 ${index + 1}`}
                />
              </Thumbnail>
            ))}
          </ThumbnailStrip>
        </ModalContent>
      </ModalOverlay>
    </ModalPortal>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

/* 상단 고정 요소 노치(상태바) 보정
 * - 앱(WebView)은 window.notchHeight → theme.mobile.notchHeight 로 주입 (userReduce.tsx)
 * - 주입값이 없는 환경(모바일 브라우저 등)은 safe-area-inset 으로 보정
 * - max() 로 둘 중 큰 값만 적용하여 이중 보정 방지 */
const notchTop = (offset: number) => ({ theme }: { theme: DefaultTheme }) =>
  `max(calc(${offset}px + ${theme.mobile.notchHeight}px), calc(${offset}px + env(safe-area-inset-top, 0px)))`;

const CloseButton = styled.button`
  position: absolute;
  top: ${notchTop(20)};
  right: 16px;
  width: 44px;
  height: 44px;
  padding: 0;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  user-select: none;
  
  &:active {
    cursor: grabbing;
  }
`;

const MainImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
`;

const SelectButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 5;
  
  img {
    width: 100%;
    height: 100%;
  }
  
  &.selected {
    opacity: 1;
  }
  
  &:hover {
    transform: scale(1.1);
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  top: ${notchTop(20)};
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 12px;
  border-radius: 20px;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
  
  &.prev {
    left: 20px;
  }
  
  &.next {
    right: 20px;
  }
`;

const ThumbnailStrip = styled.div`
  position: absolute;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  max-width: 90vw;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
`;

const Thumbnail = styled.button`
  flex: 0 0 auto;
  width: 60px;
  height: 60px;
  padding: 0;
  border: 2px solid transparent;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &.active {
    border-color: white;
  }
  
  &:hover:not(.active) {
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

export default GalleryImageModal;